import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { useStore } from '../contexts/StoreContext';
import { Modal } from '../components/ui/Modal';
import { InquiryForm } from '../components/InquiryForm';
import { Vehicle } from '../types/catalog';
import { QuickQuote, amortizationLabel, getQuickQuote } from '../utils/finance';
import { formatARS, formatDate, formatPct } from '../utils/format';
import { vehicleBrandName, vehicleLabel, vehicleText } from '../utils/catalog';
const NA = 'No disponible';
export function Compare() {
  const {
    vehicles,
    brands,
    compareIds,
    setCompareIds
  } = useStore();
  const [askFor, setAskFor] = useState<Vehicle | null>(null);
  const published = vehicles.filter(v => v.published);
  const selected = compareIds.map(id => published.find(v => v.id === id)).filter((v): v is Vehicle => !!v);
  const quotes = useMemo(() => selected.map(v => getQuickQuote(v, brands)), [selected, brands]);
  const setSlot = (i: number, id: string) => {
    const next = [...compareIds];
    if (id) next[i] = id;else next.splice(i, 1);
    setCompareIds(Array.from(new Set(next.filter(Boolean))));
  };
  const conditionsDiffer = (() => {
    const qs = quotes.filter((q): q is QuickQuote => !!q);
    if (qs.length < 2) return false;
    const key = (q: QuickQuote) => `${q.ctx.offer.tna}-${q.ctx.offer.amortization}-${q.term}-${q.ctx.offer.minDownPct}`;
    return new Set(qs.map(key)).size > 1;
  })();
  const rows: {
    label: string;
    get: (v: Vehicle, q: QuickQuote | null) => string;
  }[] = [{
    label: 'Precio de contado',
    get: v => formatARS(v.price)
  }, {
    label: 'Anticipo mínimo',
    get: (_, q) => q ? formatARS(q.down) : NA
  }, {
    label: 'Capital a financiar',
    get: (_, q) => q ? formatARS(q.principal) : NA
  }, {
    label: 'Cuota estimada',
    get: (_, q) => q?.sim ? `${q.term} × ${formatARS(q.sim.firstInstallment)}${q.ctx.offer.amortization === 'aleman' ? ' (1ª)' : ''}` : NA
  }, {
    label: 'Plazos habilitados',
    get: (_, q) => q && q.ctx.offer.terms.length ? `${q.ctx.offer.terms.join(' · ')} meses` : NA
  }, {
    label: 'Sistema',
    get: (_, q) => q ? amortizationLabel(q.ctx.offer.amortization) : NA
  }, {
    label: 'TNA',
    get: (_, q) => q ? formatPct(q.ctx.offer.tna, NA) : NA
  }, {
    label: 'CFT',
    get: (_, q) => q ? formatPct(q.ctx.offer.cft, NA) : NA
  }, {
    label: 'Estado de la condición',
    get: (_, q) => q ? q.ctx.offer.verified ? 'Oferta verificada' : 'Simulación referencial' : NA
  }, {
    label: 'Motor',
    get: v => v.engine ?? NA
  }, {
    label: 'Transmisión',
    get: v => v.transmission ?? NA
  }, {
    label: 'Consumo',
    get: v => v.consumption ?? NA
  }, {
    label: 'Seguridad',
    get: v => v.safety.length ? v.safety.join(' · ') : NA
  }, {
    label: 'Equipamiento',
    get: v => v.equipment.length ? v.equipment.join(' · ') : NA
  }, {
    label: 'Garantía',
    get: v => v.warranty ?? NA
  }, {
    label: 'Disponibilidad',
    get: v => vehicleText(v).availability || NA
  }, {
    label: 'Datos actualizados',
    get: v => formatDate(v.updatedAt)
  }];
  const slots = [0, 1, 2];
  return <div className="container-x py-8 lg:py-12" data-id="element-1230" data-mp="3m0ii1xmg42c">
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1231" data-mp="ihgb02ijzrwm">Comparador de vehículos</h1>
      <p className="mt-2 max-w-2xl text-mist" data-id="element-1232" data-mp="dj51jsw67jex">Elegí hasta tres unidades, de la misma o de distintas marcas, y comparalas lado a lado.</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-3" data-id="element-1233" data-mp="3lvss6qa1toz7">
        {slots.map(i => {
        const current = compareIds[i] ?? '';
        const disabled = i > compareIds.length;
        return <div key={i} data-id="element-1234" data-mp="3vpjhtyhww5gh">
              <label htmlFor={`cmp-${i}`} className="label" data-id="element-1235" data-mp="3qwgkrgdxedvj">Vehículo {i + 1}</label>
              <div className="flex gap-2" data-id="element-1236" data-mp="337fikavoryyp">
                <select id={`cmp-${i}`} className="input" value={current} disabled={disabled} onChange={e => setSlot(i, e.target.value)} data-id="element-1237" data-mp="3364gcfhw2z9p">
                  <option value="" data-id="element-1238" data-mp="2y6i1k1mexays">{disabled ? 'Completá el anterior' : 'Elegí una unidad'}</option>
                  {published.filter(v => v.id === current || !compareIds.includes(v.id)).map(v => <option key={v.id} value={v.id} data-id="element-1239" data-mp="2ah414t6q9m3w">{vehicleLabel(v, brands)}</option>)}
                </select>
                {current && <button onClick={() => setSlot(i, '')} className="btn btn-ghost w-11 px-0" aria-label="Quitar vehículo" data-id="element-1240" data-mp="2f9x69i63ywgi">
                    <XIcon className="h-4 w-4" data-id="element-1241" data-mp="2k9jm7zdbmqt0" />
                  </button>}
              </div>
            </div>;
      })}
      </div>

      {conditionsDiffer && <div className="mt-6 flex gap-3 rounded-xl border border-warn/30 bg-warn/5 p-4 text-sm text-warn" role="note" data-id="element-1242" data-mp="2t60wl0dv2knp">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-1243" data-mp="yu40uv8dysmu" />
          <p data-id="element-1244" data-mp="yw710839625a">
            Las cuotas se calculan con las condiciones propias de cada unidad (TNA, sistema, anticipo mínimo y plazo más largo). Como esas condiciones son
            distintas, las cuotas no son directamente comparables entre sí. Compará también el costo total en el simulador.
          </p>
        </div>}

      {selected.length === 0 ? <div className="mt-8 rounded-2xl border border-dashed border-line p-10 text-center" data-id="element-1245" data-mp="mkwhozl1wotd">
          <p className="font-semibold" data-id="element-1246" data-mp="hltc9e15sgkv">Todavía no elegiste vehículos</p>
          <p className="mt-1 text-sm text-mist" data-id="element-1247" data-mp="1f8a5o86rua1c">Usá los selectores de arriba o el botón “Comparar” en el catálogo.</p>
          <Link to="/catalogo" className="btn btn-primary mt-5" data-id="element-1248" data-mp="1a95y1lzhwkd4">Ir al catálogo</Link>
        </div> : <div className="mt-8 overflow-x-auto rounded-2xl border border-line" data-id="element-1249" data-mp="159b9vq73u8vr">
          <table className="w-full min-w-[640px] border-collapse text-sm" data-id="element-1250" data-mp="119ak59ek2b9e">
            <caption className="sr-only" data-id="element-1251" data-mp="3q0lgjl2qmzim">Comparación de vehículos seleccionados</caption>
            <thead data-id="element-1252" data-mp="3py8pmif05etf">
              <tr className="bg-surface" data-id="element-1253" data-mp="3lz8ab9vqgub6">
                <th scope="col" className="w-44 p-4 text-left align-bottom text-xs font-medium text-mist" data-id="element-1254" data-mp="3ly7017mjkbui">Característica</th>
                {selected.map(v => <th key={v.id} scope="col" className="p-4 text-left align-top font-normal" data-id="element-1255" data-mp="3sfrxvtpck82j">
                    <img src={v.images[0]} alt="" className="aspect-[4/3] w-full rounded-lg object-cover" data-id="element-1256" data-mp="3dihe7x2jvvvq" />
                    <p className="mt-3 text-xs text-mist" data-id="element-1257" data-mp="3igj5h5a4r1ia">{[vehicleBrandName(v, brands), vehicleText(v).year].filter(Boolean).join(' · ')}</p>
                    <p className="font-display text-base font-bold" data-id="element-1258" data-mp="34ocs1qyd38mj">{v.model}</p>
                    <p className="text-xs text-mist" data-id="element-1259" data-mp="39h5w0cm0acxk">{v.version}</p>
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1260" data-mp="2zqkpm303c443">
              {rows.map(r => <tr key={r.label} data-id="element-1261" data-mp="2zo8adxlpw7uq">
                  <th scope="row" className="bg-surface/60 p-4 text-left text-xs font-medium text-mist" data-id="element-1262" data-mp="2lqa0b4efixl1">{r.label}</th>
                  {selected.map((v, i) => {
              const val = r.get(v, quotes[i]);
              return <td key={v.id} className={`p-4 align-top ${val === NA ? 'italic text-mist/70' : ''} ${r.label === 'Estado de la condición' && val === 'Oferta verificada' ? 'text-ok' : ''} ${r.label === 'Estado de la condición' && val === 'Simulación referencial' ? 'text-warn' : ''}`} data-id="element-1263" data-mp="274cw0c9v4g4e" data-mpi={i}>
                        {val}
                      </td>;
            })}
                </tr>)}
              <tr data-id="element-1264" data-mp="2l3vt8a14y71x">
                <th scope="row" className="bg-surface/60 p-4" data-id="element-1265" data-mp="2l4dytx9otxmi" />
                {selected.map(v => <td key={v.id} className="p-4" data-id="element-1266" data-mp="2yvip48r61dk6">
                    <div className="grid gap-2" data-id="element-1267" data-mp="2yxdatpa9h9pb">
                      <Link to={`/vehiculo/${v.id}`} className="btn btn-ghost" data-id="element-1268" data-mp="2utfajb979suo">Ver detalle</Link>
                      <button onClick={() => setAskFor(v)} className="btn btn-primary" data-id="element-1269" data-mp="38sneehgb5p57">Solicitar asesoramiento</button>
                    </div>
                  </td>)}
              </tr>
            </tbody>
          </table>
        </div>}

      <Modal open={!!askFor} onClose={() => setAskFor(null)} title="Solicitar asesoramiento" data-id="element-1270" data-mp="f3rjjh9hm7ky">
        {askFor && <InquiryForm channel="comparador" vehicle={askFor} idPrefix="cmp" defaultMessage={`Estoy comparando: ${selected.map(v => vehicleLabel(v, brands)).join(' / ')}.`} data-id="element-1271" data-mp="o2cyl2fr34gz" />}
      </Modal>
    </div>;
}